import { useEffect, useState } from "react";
import { CurrencyInr, Coins } from "@phosphor-icons/react";
import api from "../../lib/api";

const USD_PER_INR = 1 / 83;       // approximate — used only for display

function fmtInr(n) {
  if (n == null || isNaN(n)) return "—";
  return `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}
function fmtUsd(inr) {
  if (inr == null || isNaN(inr)) return "—";
  const usd = inr * USD_PER_INR;
  if (usd < 1) return `$${usd.toFixed(2)}`;
  if (usd < 10) return `$${usd.toFixed(1)}`;
  return `$${Math.round(usd)}`;
}

/* Lightweight cost-preview chip used on each Workflow stage.

   props:
     stage        — "script" | "image" | "video" | "timeline"
     sceneCount   — number of scenes the action will iterate over
     model        — currently-selected registry model object (with cost_per_unit_inr + unit)
                    OR an array of { model, scenes } when stages mix models per scene.
     extraUnits   — optional multiplier (e.g. 2 for character + location images)
     label        — short label for the action ("Generate scripts", "Render videos"…)
     testid       — for tests
*/
export default function CostChip({ stage, sceneCount = 0, model, extraUnits = 1,
                                   label, testid = "cost-chip" }) {
  if (!model) {
    return (
      <div className="text-[10px] text-muted-foreground italic" data-testid={testid}>
        Pick a {stage} model to see the cost preview.
      </div>
    );
  }
  // For "script" the cost is per-storyboard (1 call), not per scene.
  // The registry uses `unit` ("storyboard" | "image" | "clip" | …).
  const unit = (model.unit || "").toLowerCase();
  const perScene = unit === "image" || unit === "clip" || unit === "scene";
  const scenes = Math.max(1, Number(sceneCount) || 1);
  const units = perScene ? scenes * Math.max(1, extraUnits) : 1;
  const inr = units * Number(model.cost_per_unit_inr || 0);
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border hairline text-[11px] bg-muted/30"
      data-testid={testid}
      title={`Estimated cost: ${units} × ₹${model.cost_per_unit_inr}/${model.unit} = ${fmtInr(inr)} (≈ ${fmtUsd(inr)}). Live rate from your Brand kit.`}
    >
      <CurrencyInr size={11} weight="duotone" className="text-[hsl(var(--primary))]" />
      <span className="font-medium">{label || "Est."}</span>
      <span className="text-muted-foreground">·</span>
      <span className="font-medium">{fmtInr(inr)}</span>
      <span className="text-muted-foreground">≈ {fmtUsd(inr)}</span>
      <span className="text-muted-foreground">·</span>
      <span className="text-muted-foreground">{units} × ₹{model.cost_per_unit_inr}/{model.unit}</span>
    </span>
  );
}

/* ------------------------------------------------------------------
   Cost Guide — modal popup that pulls live data from /models-registry
   + /brand-kit so it stays accurate when admin adds new models.
   ------------------------------------------------------------------ */
export function CostGuideModal({ onClose }) {
  const [registry, setRegistry] = useState(null);
  const [brand, setBrand] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [r, b] = await Promise.all([
          api.get("/admin/social/models-registry"),
          api.get("/admin/social/brand-kit"),
        ]);
        if (cancelled) return;
        setRegistry(r.data || {});
        setBrand(b.data || {});
      } catch {
        if (!cancelled) { setRegistry({}); setBrand({}); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const sec = (k) => (registry?.[k] || []).filter((m) => m.enabled !== false);
  const scriptModels = sec("script");
  const imageModels  = sec("image");
  const videoModels  = sec("video");
  const voiceModels  = sec("voice");
  const avatarModels = sec("avatar");

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-background border hairline max-w-4xl w-full p-6 space-y-5 max-h-[92vh] overflow-y-auto"
        data-testid="cost-guide-modal">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <div>
            <div className="eyebrow">Help · Cost guide</div>
            <h2 className="font-serif text-3xl mt-1">Storyboard cost guide</h2>
            <p className="text-xs text-muted-foreground mt-2 max-w-2xl">
              Live cost reference for every stage of the workflow. Numbers pulled
              from your Brand kit + Models registry — when you add a new model or
              tweak a rate, this page updates automatically. USD shown at $1 ≈ ₹83.
            </p>
          </div>
          <button onClick={onClose} className="btn-ghost px-3 py-1.5 text-xs"
            data-testid="cost-guide-close">Close</button>
        </div>

        {registry === null ? (
          <div className="text-xs text-muted-foreground py-6 text-center">Loading…</div>
        ) : (
          <>
            <GuideSection title="Stage 1 · Script" hint="One Claude call per storyboard. Independent of scene count."
              models={scriptModels} />
            <GuideSection title="Stage 2 · Image + Cast" hint="One image per scene. Add a location image to double this."
              models={imageModels} />
            <GuideSection title="Stage 3 · Video" hint="One clip per scene. Sora 2 duration choice (4 / 8 / 12 sec) triples the cost — pick wisely."
              models={videoModels} />
            <GuideSection title="Stage 4 · Voice (optional)" hint="ElevenLabs voiceover — per-scene. Charged on the ElevenLabs key, not the universal key."
              models={voiceModels} />
            <GuideSection title="Optional · Avatar (HeyGen)" hint="Replaces Sora video with an avatar-led talking head. Charged on the HeyGen key."
              models={avatarModels} />

            <FiveSceneRecipe registry={registry} brand={brand} />
            <SavingsFromLibrary registry={registry} />
            <SaveTips />
            <BudgetGuidance />
            <CapAndTopUp brand={brand} />
          </>
        )}
      </div>
    </div>
  );
}

function GuideSection({ title, hint, models }) {
  if (!models?.length) {
    return (
      <section className="card-flat p-4 space-y-2" data-testid={`guide-${title.replace(/\W+/g, "-").toLowerCase()}`}>
        <div className="eyebrow">{title}</div>
        <p className="text-[11px] text-muted-foreground">{hint}</p>
        <div className="text-[11px] text-muted-foreground italic">
          No models enabled. Add one from <b>Models registry</b> → enable to see rates.
        </div>
      </section>
    );
  }
  return (
    <section className="card-flat p-4 space-y-3" data-testid={`guide-${title.replace(/\W+/g, "-").toLowerCase()}`}>
      <div>
        <div className="eyebrow">{title}</div>
        <p className="text-[11px] text-muted-foreground mt-1">{hint}</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-muted-foreground text-[10px] uppercase tracking-wider">
              <th className="py-1.5 pr-3">Model</th>
              <th className="py-1.5 pr-3">Provider · ID</th>
              <th className="py-1.5 pr-3 text-right">₹ / unit</th>
              <th className="py-1.5 pr-3 text-right">$ / unit</th>
              <th className="py-1.5">Unit</th>
            </tr>
          </thead>
          <tbody>
            {models.map((m) => {
              const inr = Number(m.cost_per_unit_inr || 0);
              return (
                <tr key={m.id || m.model_id} className="border-t hairline">
                  <td className="py-1.5 pr-3 font-medium">{m.label}</td>
                  <td className="py-1.5 pr-3 text-muted-foreground font-mono text-[10px]">{m.provider} · {m.model_id}</td>
                  <td className="py-1.5 pr-3 text-right">{fmtInr(inr)}</td>
                  <td className="py-1.5 pr-3 text-right text-muted-foreground">{fmtUsd(inr)}</td>
                  <td className="py-1.5 text-muted-foreground">{m.unit}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function FiveSceneRecipe({ registry, brand }) {
  // Pick the default-flagged / cheapest model per stage for the recipe.
  const pick = (arr) => {
    const en = (arr || []).filter((m) => m.enabled !== false);
    return en.find((m) => m.default) || en.slice().sort((a, b) =>
      Number(a.cost_per_unit_inr || 0) - Number(b.cost_per_unit_inr || 0))[0];
  };
  const s = pick(registry.script);
  const i = pick(registry.image);
  const v = pick(registry.video);
  if (!s || !i || !v) return null;
  const scenes = 5;
  // Sora 2 rate is for an 8-second clip in the brand kit. Other durations are
  // multiples (4s = ⅓ of 8s, 12s = 1.5×). Use the brand_kit override if set.
  const sora8 = Number(brand?.sora2_inr_per_clip ?? v.cost_per_unit_inr ?? 60);
  const sora4 = sora8 / 2;       // 4s ≈ half the cost of 8s in practice
  const sora12 = sora8 * 1.5;
  const imageRate = Number(brand?.gemini_image_inr_per_gen ?? i.cost_per_unit_inr ?? 8);
  const scriptRate = Number(brand?.claude_inr_per_storyboard ?? s.cost_per_unit_inr ?? 12);
  const rows = [
    { label: "Sora 2 · 4-sec clips", per: sora4, video: scenes * sora4 },
    { label: "Sora 2 · 8-sec clips", per: sora8, video: scenes * sora8 },
    { label: "Sora 2 · 12-sec clips", per: sora12, video: scenes * sora12 },
  ];
  return (
    <section className="card-flat p-4 space-y-3" data-testid="guide-5-scene-recipe">
      <div>
        <div className="eyebrow">Typical 5-scene workflow</div>
        <p className="text-[11px] text-muted-foreground mt-1">
          Includes 1 Claude storyboard ({fmtInr(scriptRate)}), {scenes} character images
          (₹{imageRate}/each) and {scenes} video clips. Add a location image and you
          double the image cost.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-muted-foreground text-[10px] uppercase tracking-wider">
              <th className="py-1.5 pr-3">Variant</th>
              <th className="py-1.5 pr-3 text-right">₹ / scene</th>
              <th className="py-1.5 pr-3 text-right">Scripts + Images</th>
              <th className="py-1.5 pr-3 text-right">+ Video</th>
              <th className="py-1.5 pr-3 text-right">Total ₹</th>
              <th className="py-1.5 pr-3 text-right">Total $</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const sub = scriptRate + scenes * imageRate;
              const total = sub + r.video;
              return (
                <tr key={r.label} className="border-t hairline">
                  <td className="py-1.5 pr-3 font-medium">{r.label}</td>
                  <td className="py-1.5 pr-3 text-right">{fmtInr(r.per)}</td>
                  <td className="py-1.5 pr-3 text-right text-muted-foreground">{fmtInr(sub)}</td>
                  <td className="py-1.5 pr-3 text-right text-muted-foreground">{fmtInr(r.video)}</td>
                  <td className="py-1.5 pr-3 text-right font-medium">{fmtInr(total)}</td>
                  <td className="py-1.5 pr-3 text-right text-muted-foreground">{fmtUsd(total)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SavingsFromLibrary({ registry }) {
  const i = (registry.image || []).find((m) => m.enabled !== false);
  const v = (registry.video || []).find((m) => m.enabled !== false);
  const s = (registry.script || []).find((m) => m.enabled !== false);
  const scenes = 5;
  const imageRate = Number(i?.cost_per_unit_inr || 8);
  const videoRate = Number(v?.cost_per_unit_inr || 60);
  const scriptRate = Number(s?.cost_per_unit_inr || 12);
  const fullVideo = scriptRate + scenes * (imageRate + videoRate);
  const reuseScripts = scenes * imageRate + scenes * videoRate; // scripts saved
  const reuseImages  = scriptRate + scenes * videoRate;          // images saved
  const reuseBundle  = 0;                                        // bundle = $0
  return (
    <section className="card-flat p-4 space-y-3 border-l-2 border-[hsl(var(--primary))]" data-testid="guide-library-savings">
      <div>
        <div className="eyebrow inline-flex items-center gap-1.5">
          <Coins size={12} weight="duotone" className="text-[hsl(var(--primary))]" /> Savings from the Library
        </div>
        <p className="text-[11px] text-muted-foreground mt-1">
          The Library lets you re-use saved scripts, images, videos or whole-draft
          <b> bundles</b> as new scenes. Every entry you pick from the library
          replaces an LLM call → zero universal-key cost on apply.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-muted-foreground text-[10px] uppercase tracking-wider">
              <th className="py-1.5 pr-3">What you re-use</th>
              <th className="py-1.5 pr-3">Skips</th>
              <th className="py-1.5 pr-3 text-right">Remaining cost (5 scenes)</th>
              <th className="py-1.5 pr-3 text-right">You save</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t hairline">
              <td className="py-1.5 pr-3 font-medium">Saved scripts</td>
              <td className="py-1.5 pr-3 text-muted-foreground">Claude script call</td>
              <td className="py-1.5 pr-3 text-right">{fmtInr(reuseScripts)}</td>
              <td className="py-1.5 pr-3 text-right text-[hsl(var(--primary))]">{fmtInr(fullVideo - reuseScripts)} ({fmtUsd(fullVideo - reuseScripts)})</td>
            </tr>
            <tr className="border-t hairline">
              <td className="py-1.5 pr-3 font-medium">Saved images</td>
              <td className="py-1.5 pr-3 text-muted-foreground">Gemini / GPT image gen</td>
              <td className="py-1.5 pr-3 text-right">{fmtInr(reuseImages)}</td>
              <td className="py-1.5 pr-3 text-right text-[hsl(var(--primary))]">{fmtInr(fullVideo - reuseImages)} ({fmtUsd(fullVideo - reuseImages)})</td>
            </tr>
            <tr className="border-t hairline">
              <td className="py-1.5 pr-3 font-medium">Whole-draft bundle</td>
              <td className="py-1.5 pr-3 text-muted-foreground">Script + Image + Video</td>
              <td className="py-1.5 pr-3 text-right">{fmtInr(reuseBundle)} (free)</td>
              <td className="py-1.5 pr-3 text-right text-[hsl(var(--primary))]">{fmtInr(fullVideo)} ({fmtUsd(fullVideo)})</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-muted-foreground italic">
        Tip: After your first successful render, click <b>Save as bundle</b> in
        the Workflow toolbar to lock in that production for future zero-cost
        re-issues.
      </p>
    </section>
  );
}

function SaveTips() {
  const tips = [
    "Use 4-second Sora 2 clips for testing — ⅓ the cost of 8-second.",
    "Switch Video mode to Slideshow (ffmpeg only) for non-cinematic posts — ₹0 universal-key cost.",
    "Re-use saved characters via the Cast → image-lock feature so Gemini is skipped on every regenerate.",
    "Save successful scenes to the Library, then use the bulk-picker to compose new drafts at zero LLM cost.",
    "Lower scene_count from 5 → 3 for short-form (Reels / Shorts). Cuts roughly 40 % of total cost.",
    "Keep the always-visible Cost banner in view — the hard daily cap will block runaway burns.",
  ];
  return (
    <section className="card-flat p-4 space-y-2" data-testid="guide-save-tips">
      <div className="eyebrow">Cost-saving tips</div>
      <ul className="text-[11px] text-muted-foreground space-y-1.5 list-disc pl-5">
        {tips.map((t) => <li key={t}>{t}</li>)}
      </ul>
    </section>
  );
}

function BudgetGuidance() {
  const rows = [
    { label: "Single trial render (5 scenes · 8-sec Sora)", inr: 1100, usd: 13 },
    { label: "Weekly cadence (1 video / week + retries)",    inr: 5000, usd: 60 },
    { label: "Daily cadence (30 videos / month)",            inr: 33000, usd: 400 },
  ];
  return (
    <section className="card-flat p-4 space-y-2" data-testid="guide-budget">
      <div className="eyebrow">Recommended universal-key balance</div>
      <p className="text-[11px] text-muted-foreground">
        Top up via <b>Profile → Universal Key → Add Balance</b>. Enable auto
        top-up to avoid mid-render exhaustion (which surfaces as blank
        images / blank videos).
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-muted-foreground text-[10px] uppercase tracking-wider">
              <th className="py-1.5 pr-3">Cadence</th>
              <th className="py-1.5 pr-3 text-right">Approx ₹</th>
              <th className="py-1.5 pr-3 text-right">Approx $</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label} className="border-t hairline">
                <td className="py-1.5 pr-3 font-medium">{r.label}</td>
                <td className="py-1.5 pr-3 text-right">{fmtInr(r.inr)}</td>
                <td className="py-1.5 pr-3 text-right text-muted-foreground">{`$${r.usd}`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function CapAndTopUp({ brand }) {
  const dailyCap = Number(brand?.daily_inr_cap ?? 2000);
  const draftCap = Number(brand?.per_draft_inr_cap ?? 400);
  return (
    <section className="card-flat p-4 space-y-2" data-testid="guide-cap">
      <div className="eyebrow">Daily cap behaviour</div>
      <p className="text-[11px] text-muted-foreground">
        Estima enforces two hard ceilings — set under <b>Brand kit</b>:
      </p>
      <ul className="text-[11px] text-muted-foreground space-y-1.5 list-disc pl-5">
        <li><b>Daily cap:</b> {fmtInr(dailyCap)} per IST calendar day. Any further LLM call is blocked with HTTP 429 until midnight IST.</li>
        <li><b>Per-draft cap:</b> {fmtInr(draftCap)} — safety net against a runaway single draft (e.g. 10-scene 12-sec Sora 2).</li>
        <li>Both can be tuned from the <b>Brand kit</b> sub-tab. Lower them while testing, raise them once you have a stable workflow.</li>
        <li>The always-visible <b>Cost banner</b> on the Storyboard header turns amber at 50 % and red at 80 % of the daily cap.</li>
      </ul>
    </section>
  );
}
